<template>
  <section class="create-docx-quizzes">
    <div class="create-docx-quizzes__body container">
      <div class="row">
        <div class="col-12">
          <div class="create-docx-quizzes__header-title typography--color-primary text-center">
            {{ $t('publishers.create_docx_quizzes.title') }}
          </div>

          <div class="create-docx-quizzes__form">
            <!-- subject select -->
            <div class="form-group">
              <label for="subject-select"
                     class="typography--body-3 typography--color-secondary typography--font-weight-600">
                {{ $t('card_details.card_meta.subject') }}
              </label>

              <select v-model="subjectId" id="subject-select" class="form-control" :disabled="isSeriesCard">
                <option disabled value="">
                  {{ $t('card_details.card_meta.subject_placeholder') }}
                </option>
                <option v-for="sjId in subjectIds.filter(id => id !== '0' && id !== '9999')"
                        :key="sjId"
                        :value="sjId">
                  {{ getTranslatedName(subjectMap[sjId]) }}
                </option>
              </select>
            </div>

            <!-- grade select -->
            <div class="form-group">
              <label for="grade-select"
                    class="typography--body-3 typography--color-secondary typography--font-weight-600">
                {{ $t('card_details.card_meta.grade') }}
              </label>

              <select v-model="gradeId" id="grade-select" class="form-control" :disabled="isSeriesCard">
                <option disabled value="">
                  {{ $t('card_details.card_meta.grade_placeholder') }}
                </option>
                <option v-for="gId in gradeIds.filter(id => id !== '0' && id !== '9999')"
                        :key="gId"
                        :value="gId">
                  {{ getTranslatedName(gradeMap[gId]) }}
                </option>
              </select>
            </div>

            <!-- unit select -->
            <div class="form-group" v-show="showUnitSelect">
              <label for="unit-select"
                    class="typography--body-3 typography--color-secondary typography--font-weight-600">
                {{ $t('card_details.card_meta.chapter') }}
              </label>

              <select v-model="unitId" id="unit-select" class="form-control" @change="onUnitSelect" :disabled="isSeriesCard && $route.query.unit_id">
                <option disabled value="">
                  {{ $t('card_details.card_meta.chapter_placeholder') }}
                </option>
                <option v-for="unit in unitList" :key="unit.id" :value="unit.id">
                  {{ unit.name }}
                </option>
              </select>
            </div>

            <!-- core concept select -->
            <div class="form-group" v-show="showCoreConceptSelect">
              <label for="core-concept-select"
                     class="typography--body-3 typography--color-secondary typography--font-weight-600">
                {{ $t('card_details.card_meta.core_concept') }}
              </label>

              <select v-model="coreConceptId" id="core-concept-select" class="form-control" :disabled="isSeriesCard && $route.query.core_concept_id">
                <option disabled value="">
                  {{ $t('card_details.card_meta.core_concepts_placeholder') }}
                </option>
                <option v-for="coreConcept in coreConceptList" :key="coreConcept.id" :value="coreConcept.id">
                  {{ coreConcept.name }}
                </option>
              </select>
            </div>

            <!-- video course id -->
            <div class="form-group">
              <label
                for="video-course-id"
                class="typography--body-3 typography--color-secondary typography--font-weight-600"
              >
                {{ $t('card_details.card_meta.video_course') }}
              </label>

              <input
                type="number"
                id="video-course-id"
                class="form-control"
                v-model.trim="videoCourseId"
              >
            </div>

            <!-- card title -->
            <div class="form-group">
              <label for="card-title-input"
                     class="typography--body-3 typography--color-secondary typography--font-weight-600">
                {{ $t('card_details.card_meta.title') }}
              </label>

              <input
                type="text"
                id="card-title-input"
                class="form-control"
                maxlength="50"
                :placeholder="$t('card_details.card_meta.title_placeholder')"
                v-model.trim="cardTitle"
              >
            </div>

            <!-- card goal -->
            <div class="form-group">
              <label for="card-goal-input"
                     class="typography--body-3 typography--color-secondary typography--font-weight-600">
                {{ $t('card_details.card_meta.goal') }}
              </label>

              <textarea
                id="card-goal-input"
                class="form-control"
                rows="5"
                maxlength="5000"
                :placeholder="$t('card_details.card_meta.goal_placeholder')"
                v-model="cardGoal"
              />
            </div>

            <!-- file input -->
            <div class="form-group" v-if="!docxParsingCompleted">
              <div class="create-docx-quizzes__upload-area mx-auto text-center">
                <label class="create-docx-quizzes__upload-input text-center clickable w-100">
                  <input
                    type="file"
                    class="no-display"
                    :accept="validFileType"
                    @change="onFileInputChange"
                    :disabled="loading"
                    ref="docxFileInput"
                  />
                </label>
              </div>

              <div class="create-docx-quizzes__upload-text typography--headline typography--color-secondary text-center">
                {{ $t('publishers.create_docx_quizzes.upload_text') }}
              </div>

              <div class="mt-40 text-center" v-if="loading">
                <LoadingBounce></LoadingBounce>
                <div class="typography--body-2 typography--color-disabled typography--font-weight-bold pb-5">
                  {{ $t('common.reading_file') }}
                </div>
              </div>
            </div>

            <div class="text-center mt-40" v-else>
              <div class="typography--content-body typography--color-primary mb-12">
                {{ $t('publishers.create_docx_quizzes.file_info.name', { name: docxFileName }) }}
              </div>
              <div class="typography--body-1 typography--color-primary mb-2">
                {{ $t('publishers.create_docx_quizzes.file_info.quiz_count', { count: fileInfo.quizCount }) }}
              </div>
              <div class="typography--body-1 typography--color-primary mb-2">
                {{ $t('publishers.create_docx_quizzes.file_info.multiple_choice_count', { count: fileInfo.multipleChoiceCount }) }}
              </div>
              <div class="typography--body-1 typography--color-primary mb-2">
                {{ $t('publishers.create_docx_quizzes.file_info.text_entry_count', { count: fileInfo.textEntryCount }) }}
              </div>
              <div class="typography--body-1 typography--color-primary mb-2">
                {{ $t('publishers.create_docx_quizzes.file_info.image_count', { count: fileInfo.imageCount }) }}
              </div>
            </div>

            <div class="text-center mt-40">
              <button class="btn btn-primary btn-full create-docx-quizzes__create-btn"
                      :disabled="createDisabled"
                      @click="create()">
                <span v-if="!busy">{{ $t('publishers.create_docx_quizzes.create') }}</span>
                <LoadingBounce v-else/>
              </button>
            </div>

            <div class="text-center mt-2">
              <router-link class="btn btn-secondary btn-full create-docx-quizzes__cancel-btn typography--color-secondary"
                           :to="prevLink">
                {{ $t('btn.redirect_to_root') }}
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="modal-area">
      <b-modal id="docx-type-alert"
               class="basic-modal no-header-modal hide-cancel-btn"
               :hide-header="true"
               :ok-title="$t('common.confirm')">
        <div v-html="$t('common.modal.docx_type_alert.message')"/>
      </b-modal>

      <b-modal id="file-reader-unsupported-alert"
               class="basic-modal no-header-modal hide-cancel-btn"
               :hide-header="true"
               :ok-title="$t('common.confirm')">
        <div v-html="$t('common.modal.file_reader_unsupported.message')"/>
      </b-modal>

      <b-modal id="basic-error-alert"
               class="basic-modal no-header-modal hide-cancel-btn"
               :hide-header="true"
               :ok-title="$t('common.confirm')">
        <div v-html="$t('common.modal.basic_error_alert.message')"/>
        <div class="break-word-all mt-3">
          {{ errMsg }}
        </div>
      </b-modal>
    </div>
  </section>
</template>

<script>
  import { mapGetters, mapActions } from 'vuex';
  import { isNil, isEmpty, keyBy, get, trim, keys, find, sortBy, filter, map, isEqual, toString, first, omitBy, omit, toLower } from 'lodash';
  import eachSeries from 'async-es/eachSeries';
  import mammoth from 'mammoth/mammoth.browser.min';
  import $ from 'jquery';
  import publisherOnly from '@/mixins/publisherOnly';
  import labels from '@/mixins/labels';

  export default {
    name: 'create-docx-quizzes',

    mixins: [
      publisherOnly,
      labels,
    ],

    data() {
      return {
        subjectId: '',
        gradeId: '',
        unitId: '',
        coreConceptId: '',
        seriesId: null,
        chapterId: null,
        chapterPosition: null,
        chapterName: null,
        cardTitle: '',
        cardGoal: '',
        unitList: [],
        coreConceptList: [],
        validFileType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        docxFileBuffer: null,
        docxFileName: null,
        imageList: [],
        pageList: [],
        busy: false,
        loading: false,
        docxParsingCompleted: false,
        errMsg: '',
        videoCourseId: null,
      };
    },

    computed: {
      ...mapGetters([
        'currentPublisherInfo',
        'currentUser',
        'subjectMap',
        'subjectIds',
        'gradeMap',
        'gradeIds',
      ]),

      prevLink() {
        if (this.isSeriesCard && this.seriesId) {
          return {
            name: 'publishers.series.cards',
            params: {
              series_id: this.seriesId,
            },
          };
        }

        return '/publishers/non-series-cards';
      },

      selectedSubjectGrade() {
        return `${this.subjectId}&${this.gradeId}`;
      },

      showUnitSelect() {
        return !isEmpty(this.unitList);
      },

      showCoreConceptSelect() {
        return !isEmpty(this.coreConceptList);
      },

      unitsById() {
        return keyBy(this.unitList, 'id');
      },

      fileInfo() {
        return {
          quizCount: this.pageList.length,
          multipleChoiceCount: filter(this.pageList, page => (page.page_type_id === 2)).length,
          textEntryCount: filter(this.pageList, page => (page.page_type_id === 3)).length,
          imageCount: this.imageList.length,
        };
      },

      createDisabled() {
        if (this.subjectId === '') {
          return true;
        }

        if (!isEmpty(this.unitList) && this.unitId === '') {
          return true;
        }

        if (!isEmpty(this.coreConceptList) && this.coreConceptId === '') {
          return true;
        }

        if (isEmpty(this.cardTitle)) {
          return true;
        }

        if (isNil(this.docxFileBuffer)) {
          return true;
        }

        if (!this.docxParsingCompleted) {
          return true;
        }

        return false;
      },

      isSeriesCard() {
        return this.$route.name === 'publishers.series.chapters.createDocxQuizzes';
      },
    },

    watch: {
      selectedSubjectGrade(value) {
        if (value.match(/^[0-9]+&9999$/) || value.match(/^9999&[0-9]+$/)) {
          return null;
        }

        const params = {
          subject_id: this.subjectId,
          grade_id: this.gradeId,
          unit_id: this.$route.query.unit_id,
        };

        if (!isNil(value.match(/[0-9]+&[0-9]+/))) {
          // if (this.getSchoolYear(this.gradeMap[this.gradeId]) > 6) {
          //   return this.fetchCoreConcepts(params)
          //     .then((coreConceptList) => {
          //       this.coreConceptList = coreConceptList || [];
          //       this.unitList = [];

          //       const coreConceptsById = keyBy(this.coreConceptList, 'id');

          //       if (isNil(get(coreConceptsById, this.coreConceptId))) {
          //         this.coreConceptId = '';
          //       }
          //     });
          // }

          return Promise.all([
            this.fetchCoreConcepts(params),
            this.fetchUnits(params),
          ])
          .then(([coreConceptList, unitList]) => {
            this.coreConceptList = coreConceptList || [];
            this.unitList = unitList || [];

            const coreConceptsById = keyBy(this.coreConceptList, 'id');
            const unitsById = keyBy(this.unitList, 'id');

            if (isNil(get(coreConceptsById, this.coreConceptId))) {
              this.coreConceptId = '';
            }

            if (isNil(get(unitsById, this.unitId))) {
              this.unitId = '';
            }
          });
        }

        return null;
      },
    },

    methods: {
      ...mapActions([
        'uploadNewCardPageImage',
        'createNewMyCardPage',
        'fetchCoreConcepts',
        'createNewMyCard',
        'addCardToSeries',
        'updateMyPage',
        'fetchUnits',
        'createVideoCourseCards',
      ]),

      onUnitSelect() {
        const coreConceptId = get(this.unitsById, `${this.unitId}.core_concept_id`);

        if (!isNil(coreConceptId)) {
          this.coreConceptId = coreConceptId;
        }
      },

      clearFileInput() {
        const docxFileInput = this.$refs.docxFileInput;
        docxFileInput.value = '';
      },

      onFileInputChange(event) {
        if (this.loading) {
          return null;
        }

        const files = event.target.files || event.dataTransfer.files;

        if (!files.length) {
          return null;
        }

        const file = files[0];

        if (!file || file.size === undefined || file.type === undefined) {
          return null;
        }

        if (get(file, 'type', '').indexOf(this.validFileType) === -1) {
          return this.$root.$emit('show::modal', 'docx-type-alert');
        }

        if (!window.FileReader) {
          return this.$root.$emit('show::modal', 'file-reader-unsupported-alert');
        }

        const reader = new window.FileReader();

        reader.onload = (loadEvent) => {
          this.loading = true;
          this.docxFileName = file.name;
          this.docxFileBuffer = loadEvent.target.result;

          this.convertDocxToHtml()
            .then((result) => {
              const doc = $(result.value);
              this.pageList = this.parsePages(doc);
              this.docxParsingCompleted = true;
            })
            .catch((err) => {
              this.errMsg = toString(err);
              this.$root.$emit('show::modal', 'basic-error-alert');
              console.error('errMsg:', this.errMsg);

              this.docxFileName = null;
              this.docxFileBuffer = null;
              this.pageList = [];
              this.loading = false;
              this.clearFileInput();
            });
        };

        return reader.readAsArrayBuffer(file);
      },

      convertDocxToHtml() {
        const options = {
          convertImage: mammoth.images.imgElement(image => (
            image.read()
              .then((imageBuffer) => {
                const dataImgId = `tmp_${Math.floor(Math.random() * 1000000)}`;
                const imageBlob = new Blob([imageBuffer], { type: image.contentType });
                const imageInfo = {
                  image_file: imageBlob,
                  key: dataImgId,
                };

                this.imageList.push(imageInfo);

                return { src: `${dataImgId}` };
              })
          )),
          styleMap: [
            'u => u',
          ],
        };

        return mammoth.convertToHtml({ arrayBuffer: this.docxFileBuffer }, options);
      },

      initNewPage() {
        return {
          author_id: this.currentUser.id,
          author_name: this.currentUser.name,
          publisher_id: this.currentPublisherInfo.id,
          publisher_name: this.currentPublisherInfo.name,
          page_type_id: null,
          language: '',
          content: '',
          description: '',
          answers: [],
          answer_view_type: '',
          position: null,
          is_copied_page: false,
          is_quiz: true,
          audio_count: 0,
          video_count: 0,
          image_count: 0,
          is_autograde: true,
          is_store_page: true,
          score: 1,
          images: [],
        };
      },

      handlePageType(page, text) {
        /* eslint-disable no-param-reassign */
        switch (text) {
          case '단답형':
            page.page_type_id = 3;
            break;
          case '객관식':
            page.page_type_id = 2;
            break;
          default:
            throw new Error(`INCORRECT PAGE TYPE: ${text}`);
        }
        /* eslint-enable */
      },

      handleLanguage(page, text) {
        /* eslint-disable no-param-reassign */
        if (!isEmpty(text)) {
          page.language = toLower(text);
        }
        /* eslint-enable */
      },

      handleContent(page, label, text, elem) {
        /* eslint-disable no-param-reassign */
        const imgs = $('img', elem);

        if (imgs.length > 1) {
          throw new Error('ONLY ONE IMAGE IS ALLOWED PER A LINE.');
        }

        if (imgs.length > 0 && !isEmpty(text)) {
          throw new Error('IMAGE AND TEXT ARE NOT ALLOWED TO BE ON THE SAME LINE.');
        }

        if (imgs.length > 0) {
          const imgDataId = imgs.attr('src');
          const imgElem = `<figure><img src="" data-image-id="${imgDataId}"></figure>`;
          const imageInfo = find(this.imageList, ({ key }) => (key === imgDataId));

          page[label] += imgElem;
          page.image_count += 1;
          page.images.push(imageInfo);
        } else {
          page[label] += elem.outerHTML;
        }
        /* eslint-enable */
      },

      handleAnswers(page, text, isCorrect) {
        if (!isEmpty(text)) {
          const answer = {
            content: text,
            is_correct: isCorrect,
          };

          page.answers.push(answer);
        }
      },

      handleAnswerViewType(page, text) {
        /* eslint-disable no-param-reassign */
        switch (text) {
          case '글자버튼':
            page.answer_view_type = 'list';
            break;
          case '숫자버튼':
            page.answer_view_type = 'control';
            break;
          default:
            throw new Error(`INCORRECT ANSWER VIEW TYPE: ${text}`);
        }
        /* eslint-enable */
      },

      isValidPage(page) {
        const pageTypeId = get(page, 'page_type_id');
        const answerCount = get(page.answers, 'length');
        const correctAnswerCount = get(filter(page.answers, 'is_correct'), 'length');

        if (pageTypeId === 2) {
          return (
            !isEmpty(page.language) &&
            !isEmpty(page.content) &&
            !isEmpty(page.answers) &&
            !isEmpty(page.answer_view_type) &&
            correctAnswerCount === 1 &&
            answerCount > 1 &&
            answerCount < 6
          );
        }

        if (pageTypeId === 3) {
          return (
            !isEmpty(page.language) &&
            !isEmpty(page.content) &&
            !isEmpty(page.answers)
          );
        }

        return false;
      },

      isEmptyPage(page) {
        return isEqual(omit(page, 'position'), omit(this.initNewPage(), 'position'));
      },

      parsePages(doc) {
        const templateLabels = {
          '[유형]': 'page_type_id',
          '[언어]': 'language',
          '[문제]': 'content',
          '[오답]': 'answers',
          '[정답]': 'correct_answers',
          '[보기선택방법]': 'answer_view_type',
          '[해설]': 'description',
        };
        const labelKeys = keys(templateLabels);
        const pageList = [];
        let page = this.initNewPage();
        let label = '';

        doc.each((i, elem) => {
          const text = trim($(elem).text());

          if (text === '[유형]') {
            if (this.isValidPage(page)) {
              page.answers = sortBy(page.answers, 'content');
              pageList.push(page);
            } else if (!this.isEmptyPage(page)) {
              throw new Error(`IS INCORRECT PAGE: ${JSON.stringify(page)}`);
            }

            page = this.initNewPage();
            page.position = pageList.length + 1;
          }

          if (labelKeys.indexOf(text) !== -1) {
            label = templateLabels[text];

            return null;
          }

          switch (label) {
            case 'page_type_id':
              return this.handlePageType(page, text);
            case 'language':
              return this.handleLanguage(page, text);
            case 'content':
            case 'description':
              return this.handleContent(page, label, text, elem);
            case 'answers':
              return this.handleAnswers(page, text, false);
            case 'correct_answers':
              return this.handleAnswers(page, text, true);
            case 'answer_view_type':
              return this.handleAnswerViewType(page, text);
            default:
              return null;
          }
        });

        if (this.isValidPage(page)) {
          page.answers = sortBy(page.answers, 'content');
          pageList.push(page);
        } else if (!this.isEmptyPage(page)) {
          throw new Error(`IS INCORRECT PAGE: ${JSON.stringify(page)}`);
        }

        return pageList;
      },

      createNewCard() {
        const unitsById = keyBy(this.unitList, 'id');
        const params = {
          title: this.cardTitle || '제목없음',
          goal: this.cardGoal,
          subject_id: this.subjectId,
          grade_id: this.gradeId || null,
          unit_id: this.unitId || undefined,
          chapter_position: this.chapterPosition || get(unitsById, `${this.unitId}.position`),
          chapter_name: this.chapterName || get(unitsById, `${this.unitId}.name`),
          core_concept_id: this.coreConceptId,
          author_id: this.currentUser.id,
          author_name: this.currentUser.name,
          publisher_id: this.currentPublisherInfo.id,
          publisher_name: this.currentPublisherInfo.name,
          share_level: 2,
          page_count: this.pageList.length,
          quiz_count: this.pageList.length,
          image_count: this.imageList.length,
          audio_count: 0,
          video_count: 0,
          is_autograde: true,
          language: get(first(this.pageList), 'language'),
        };

        return this.createNewMyCard(params);
      },

      createNewPage(page, cardId) {
        const omittedPage = omitBy(page, (value, key) => (
          key === 'images' || value === ''
        ));

        return this.createNewMyCardPage({ page: omittedPage, cardId })
          .then((newPage) => {
            const updateInfo = {
              content: newPage.content,
              description: newPage.description || '',
            };

            return new Promise((resolve, reject) => (
              eachSeries(page.images, (image, callback) => (
                this.uploadNewCardPageImage({ pageId: newPage.id, imageFile: image.image_file })
                  .then((newImage) => {
                    const oldAttr = `data-image-id="${image.key}"`;
                    const newAttr = `data-image-id="${newImage.id}"`;

                    if (updateInfo.content.indexOf(oldAttr) !== -1) {
                      updateInfo.content = updateInfo.content.replace(oldAttr, newAttr);
                    }

                    if (updateInfo.description.indexOf(oldAttr) !== -1) {
                      updateInfo.description = updateInfo.description.replace(oldAttr, newAttr);
                    }

                    return callback();
                  })
                  .catch(callback)
              ), (err) => {
                if (err) {
                  return reject(err);
                }

                return resolve({ pageId: newPage.id, updateInfo });
              })
            ));
          })
          .then(({ pageId, updateInfo }) => (
            this.updateMyPage({ pageId, updateInfo })
          ));
      },

      create() {
        if (this.busy) {
          return null;
        }

        this.busy = true;

        return this.createNewCard()
          .then(newCard => (
            Promise.all(map(this.pageList, page => (
              this.createNewPage(page, newCard.id)
            )))
            .then(() => (newCard))
          ))
          .then((newCard) => {
            if (!this.isSeriesCard) {
              return newCard;
            }

            const params = {
              subject_id: this.subjectId,
              grade_id: this.gradeId,
              series_id: this.seriesId,
              chapter_id: this.chapterId,
              chapter_name: this.chapterName,
              chapter_position: this.chapterPosition,
              core_concept_id: this.coreConceptId || null,
              unit_id: this.unitId || null,
              card_id: newCard.id,
            };

            return this.addCardToSeries(params)
              .then(() => (newCard));
          })
          .then((newCard) => {
            if (!isNil(this.videoCourseId)) {
              return this.createVideoCourseCards({
                video_course_id: this.videoCourseId,
                card_id: newCard.id,
              }).then(() => newCard);
            }

            return newCard;
          })
          .then(newCard => (
            this.$router.push(`/my-cards/${newCard.id}`)
          ));
      },
    },

    mounted() {
      if (this.isSeriesCard) {
        this.chapterId = this.$route.params.chapter_id;
        this.seriesId = this.$route.params.series_id;
        this.unitId = this.$route.query.unit_id;
        this.coreConceptId = this.$route.query.core_concept_id;
        this.chapterPosition = this.$route.query.chapter_position;
        this.chapterName = this.$route.query.chapter_name;
        this.subjectId = this.$route.query.subject_id;
        this.gradeId = this.$route.query.grade_id;
      }
    },
  };
</script>

<style lang="scss" scoped>
  .create-docx-quizzes {
    .create-docx-quizzes__body {
      max-width: 400px;
      padding-bottom: 100px;
    }

    .create-docx-quizzes__header-title {
      margin-top: 60px;
      font-size: 28px;
      line-height: 1.79;
    }

    .create-docx-quizzes__form {
      margin-top: 40px;

      .form-group .form-control {
        font-size: 14px;
      }

      .form-group input.form-control {
        height: 47px;
      }
    }

    .create-docx-quizzes__upload-area {
      width: 185px;
    }

    .create-docx-quizzes__upload-input {
      height: 185px;
      background-image: url('/static/images/baseline-cloud_upload-grey.svg');
      background-position: center;
      background-repeat: no-repeat;
      background-size: cover;
      transition: background-image 0.2s ease-in-out;
      margin-bottom: -10px;

      &:hover {
        background-image: url('/static/images/baseline-cloud_upload-black.svg');
      }
    }

    .create-docx-quizzes__create-btn,
    .create-docx-quizzes__cancel-btn {
      font-size: 16px;
      line-height: 1.88;
      font-weight: 400;
      padding-top: 5px;
      padding-bottom: 5px;
      border-radius: 4px;
    }
  }
</style>



// WEBPACK FOOTER //
// src/app/publishers/views/CreateDocxQuizzes.vue